<template>
  <div>
    <div class="w-screen h-screen bg-white">
      <img
        src="@/assets/images/logo/logo.png"
        alt="malwation-logo"
        class="w-48 pb-10"
      />
      <div class="space-y-4 md:space-y-0 md:space-x-4 md:flex">
        <div class="flex flex-col flex-1 space-y-4">
          <div
            class="flex flex-col p-8 space-y-6 bg-white border border-gray-100 rounded-lg shadow-xl"
          >
            <h1 class="text-center text-gray-700 text-md md:text-xl">Simulation Informations</h1>
            <div class="flex flex-col">
              <div class="flex justify-between mb-2" v-if="simulation.tag">
                <span>Tag</span>
                <span class="font-semibold">{{ simulation.tag }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span>Operating System</span>
                <span class="font-semibold">{{
                  simulation.operatingSystem.name
                }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span>Programming Language</span>
                <span class="font-semibold">{{
                  simulation.language.name
                }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span>Protection</span>
                <span class="font-semibold">{{
                  simulation.protection.name
                }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span>Attack Type</span>
                <span class="font-semibold">{{ simulation.attackType }}</span>
              </div>
              <div
                class="flex justify-between mb-2"
                v-if="simulation.attackType === 'mitre'"
              >
                <span>Techniques</span>
                <span class="font-semibold">{{
                  simulation.techniques.length > 3
                    ? simulation.techniques.slice(0, 3) + "..."
                    : simulation.techniques
                }}</span>
              </div>
              <div class="flex justify-between mb-2" v-else>
                <span>Custom Attack</span>
                <span class="font-semibold">{{
                  simulation.attackName.name
                }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span>Created At</span>
                <span class="font-semibold">{{
                  simulation.createdAt | moment("DD/MM/YYYY HH:mm")
                }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span>Last Update</span>
                <span class="font-semibold">{{
                  simulation.createdAt | moment("DD/MM/YYYY HH:mm")
                }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-1">
          <div
            class="container flex flex-col p-8 space-y-4 bg-white border border-gray-100 rounded-lg shadow-xl"
          >
            <div class="flex flex-col items-center justify-center">
              <h5 class="text-gray-700 text-md md:text-xl">Simulation Flow Chart</h5>
              <div class="container pt-12 mx-auto text-center">
                <div class="flex items-center justify-center">
                  <div class="text-center">
                    <div class="flex flex-col items-center justify-center">
                      <v-icon
                        :name="`brands/${simulation.operatingSystem.icon}`"
                        :title="simulation.operatingSystem.name"
                        scale="2"
                        class="block m-auto text-gray-700 fill-current"
                      ></v-icon>
                      <div class="text-gray-600">
                        <p>{{ simulation.operatingSystem.name }}</p>
                      </div>
                    </div>
                    <ul class="flex flex-row justify-center mt-10">
                      <div
                        class="absolute h-10 -mt-10 border-l-2 border-gray-400"
                      ></div>
                      <li class="relative p-6">
                        <div
                          class="absolute top-0 h-8 border-t-2 border-gray-400"
                          style="left: 50%; right: 50%"
                        ></div>
                        <div class="relative flex justify-center">
                          <div
                            class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"
                          ></div>
                          <div class="text-center">
                            <div
                              class="flex flex-col items-center justify-center"
                            >
                              <template v-if="simulation.language.logo">
                                <img
                                  :src="getImage(simulation.language.logo)"
                                  class="block p-1 m-auto rounded shadow-md cursor-pointer"
                                  style="max-width: 150px; height: 3.5em"
                                />
                              </template>
                              <template v-else>
                                <i
                                  :class="`icon-${simulation.language.icon}`"
                                  class="block text-gray-700 fill-current"
                                  style="font-size: 2em"
                                ></i>
                              </template>
                              <div class="text-gray-600">
                                <p>{{ simulation.language.name }}</p>
                              </div>
                            </div>
                            <ul class="flex flex-row justify-center mt-10">
                              <div
                                class="absolute h-10 -mt-10 border-l-2 border-gray-400"
                              ></div>
                              <li class="relative p-6">
                                <div
                                  class="absolute top-0 h-8 border-t-2 border-gray-400"
                                  style="left: 50%; right: 0px"
                                ></div>
                                <div class="relative flex justify-center">
                                  <div
                                    class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"
                                  ></div>
                                  <div class="text-center">
                                    <div
                                      class="flex flex-col items-center justify-center"
                                    >
                                      <div class="w-32 h-8">
                                        <img
                                          :src="
                                            getImage(simulation.protection.logo)
                                          "
                                          class="block m-auto"
                                          style="
                                            width: 100%;
                                            height: 100%;
                                            object-fit: contain;
                                          "
                                        />
                                      </div>
                                      <div class="text-gray-600">
                                        <p>{{ simulation.protection.name }}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <li class="relative p-6">
                                <div
                                  class="absolute top-0 h-8 border-t-2 border-gray-400"
                                  style="left: 0px; right: 50%"
                                ></div>
                                <div class="relative flex justify-center">
                                  <div
                                    class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"
                                  ></div>
                                  <div class="text-center">
                                    <div
                                      class="flex flex-col items-center justify-center"
                                    >
                                      <div class="w-32 h-8">
                                        <img
                                          class="block m-auto"
                                          :src="
                                            require('@/assets/images/mitre.png')
                                          "
                                          alt="Mitre Attack"
                                          style="
                                            width: 100%;
                                            height: 100%;
                                            object-fit: contain;
                                          "
                                          v-if="
                                            simulation.attackType === 'mitre'
                                          "
                                        />
                                        <img
                                          class="block m-auto"
                                          :src="
                                            require('@/assets/images/custom-attack.png')
                                          "
                                          alt="Custom Attack"
                                          style="
                                            width: 100%;
                                            height: 100%;
                                            object-fit: contain;
                                          "
                                          v-else
                                        />
                                      </div>
                                      <div class="text-gray-600">
                                        <p
                                          v-if="
                                            simulation.attackType === 'mitre'
                                          "
                                        >
                                          Mitre Att&ck
                                        </p>
                                        <p v-else>Custom Attack</p>
                                      </div>
                                    </div>
                                    <ul
                                      class="flex flex-row justify-center mt-10"
                                      v-if="simulation.attackType === 'custom'"
                                    >
                                      <div
                                        class="absolute h-10 -mt-10 border-l-2 border-gray-400"
                                      ></div>
                                      <li class="relative p-6">
                                        <div
                                          class="absolute top-0 h-8 border-t-2 border-gray-400"
                                          style="left: 50%; right: 50%"
                                        ></div>
                                        <div
                                          class="relative flex justify-center"
                                        >
                                          <div
                                            class="absolute top-0 h-6 -mt-6 border-l-2 border-gray-400"
                                          ></div>
                                          <div class="text-center">
                                            <div
                                              class="flex flex-col items-center justify-center"
                                            >
                                              <div class="w-32 h-8">
                                                <img
                                                  class="block m-auto"
                                                  :src="
                                                    getImage(
                                                      simulation.attackName.logo
                                                    )
                                                  "
                                                  :alt="
                                                    simulation.attackName.name
                                                  "
                                                  style="
                                                    width: 100%;
                                                    height: 100%;
                                                    object-fit: contain;
                                                  "
                                                />
                                              </div>
                                              <div class="text-gray-600">
                                                <p>
                                                  {{
                                                    simulation.attackName.name
                                                  }}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="w-screen h-screen bg-white" v-if="simulation.logs.length > 0">
      <div
        class="flex flex-col w-full p-8 space-y-8 bg-white border border-gray-100 rounded-lg shadow-xl"
      >
        <h1 class="text-2xl text-gray-700">Simulation Logs</h1>
        <div class="relative">
          <div
            class="absolute top-0 z-10 h-full border-r-2 border-gray-300"
            style="left: 19px;"
          ></div>
          <ul class="p-0 m-0 list-none">
            <li class="mb-6" v-for="log in simulation.logs" :key="log._id">
              <div class="flex items-center mb-1">
                <div class="z-20 flex items-center justify-center w-12 h-12 text-white rounded-full" :class="getColor(log.type)">
                    <i class="text-lg" :class="getIcon(log.type)" />
                </div>
                <div class="flex flex-col flex-1 ml-6 font-medium">
                  <p class="text-gray-700">{{ log.event }}</p>
                  <small class="text-gray-600 activity-e-time">{{ log.date | moment('from', 'now') }}</small>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["simulation"],
  methods: {
    getImage(image_id) {
      return `${process.env.VUE_APP_BASE_URL}/images/${image_id}`;
    },
    getIcon(type) {
      switch (type) {
        case "success":
          return "fa fa-check-circle";
        case "fail":
          return "fa fa-times";
        case "info":
          return "fa fa-info";
        default:
          return "fa fa-align-left";
      }
    },
    getColor(type) {
      switch (type) {
        case "success":
          return "bg-green-600";
        case "fail":
          return "bg-red-600";
        case "info":
          return "bg-yellow-600";
        default:
          return "bg-blue-700";
      }
    },
  },
};
</script>